import { Body, Box, Heading, IconButton, system } from '@hover/blueprint';
import { chakra } from '@hover/blueprint/chakra';
import { iPause, iPlay, iUpload } from '@hover/icons';
import React, { useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import type { TranslationNamespaceType } from 'src/i18n/types';
import { FullWidthContainer } from 'src/modules/pro/components/FullWidthContainer';
import { Button } from 'src/shared/components';
import { useTranslate } from 'src/shared/hooks';
import { useIsMobileSize } from 'src/shared/hooks/useIsMobileSize';
import type { ButtonPressed } from 'src/shared/telemetry';

const Video = chakra('video', {
  baseStyle: {
    borderRadius: 400,
  },
});

type VideoSectionProps = {
  translationName: TranslationNamespaceType;
  primaryBackgroundColor: string;
  secondaryBackgroundColor: string;
  desktopVideoSource: string;
  mobileVideoSource: string;
  onAddPhotoClick: () => void;
  ctaTrackingData: Partial<ButtonPressed>;
};

const VideoSection = ({
  translationName,
  primaryBackgroundColor,
  secondaryBackgroundColor,
  desktopVideoSource,
  mobileVideoSource,
  onAddPhotoClick,
  ctaTrackingData,
}: VideoSectionProps) => {
  const t = useTranslate(translationName);
  const isMobile = useIsMobileSize();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isPlaying, setIsPlaying] = useState(true);

  const handlePlayPause = (): void => {
    if (videoRef.current) {
      if (isPlaying) {
        void videoRef.current.pause();
      } else {
        void videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const { job_id: _, ...relevantTrackingData } = ctaTrackingData;

  return (
    <FullWidthContainer
      backgroundColor={primaryBackgroundColor}
      flexDirection="column"
      paddingX={{ base: 500, desktop: 1200 }}
    >
      <Box
        flexDirection="column"
        paddingY={{ base: 1000, tablet: 1200 }}
        width="100%"
      >
        <Heading
          color="neutral.0"
          size={{ base: 'large', tablet: 'xl' }}
          sx={{ marginBottom: '0 !important' }}
          textAlign={{ base: 'left', tablet: 'center' }}
        >
          <Trans
            components={{
              LineBreak: (
                <system.br
                  display={{
                    base: 'none',
                    width250: 'inline',
                    tablet: 'none',
                    width350: 'inline',
                  }}
                />
              ),
            }}
            i18nKey="video.header"
            t={t}
          />
        </Heading>
        <Box
          marginTop={{ base: 700, tablet: 900 }}
          position="relative"
          width="100%"
        >
          <Video
            autoPlay
            loop
            muted
            playsInline
            ref={videoRef}
            src={isMobile ? mobileVideoSource : desktopVideoSource}
            width="100%"
          />
          <IconButton
            bottom={400}
            colorScheme="light"
            icon={isPlaying ? iPause : iPlay}
            label="playButton"
            onClick={handlePlayPause}
            position="absolute"
            right={400}
            size="small"
          />
        </Box>

        <Box
          alignItems="center"
          backgroundColor={secondaryBackgroundColor}
          borderRadius={400}
          display={{ base: 'none', tablet: 'flex' }}
          justifyContent="space-between"
          marginTop={{ tablet: 500 }}
          paddingX={700}
          paddingY={500}
          width="100%"
        >
          <Body color="neutral.0">{t('video.description')}</Body>
          <Button
            colorScheme="light"
            iconBefore={iUpload}
            onClick={onAddPhotoClick}
            optionalTrackingProperties={relevantTrackingData}
            size="large"
          >
            {t('video.buttonText')}
          </Button>
        </Box>
        <Button
          colorScheme="light"
          display={{ base: 'flex', tablet: 'none' }}
          iconBefore={iUpload}
          marginTop={700}
          onClick={onAddPhotoClick}
          optionalTrackingProperties={relevantTrackingData}
          size="large"
          width="100%"
        >
          {t('video.buttonText')}
        </Button>
      </Box>
    </FullWidthContainer>
  );
};

export { VideoSection };
